import React from 'react';
import { useTranslation } from '../../i18n';
import ImageWithFallback from './ImageWithFallback';
import { DrunkQuestionText } from './DrunkQuestionText/DrunkQuestionText';
import { pickImageAnimationForSeed } from '../../constants/drunkVisionAnimations';

interface ConditionalQuestionDisplayProps {
  /** Whether the host is playing (if true, show question; if false, hide it) */
  hostIsPlaying: boolean;
  /** The question text to display */
  question: string;
  /** Optional image URL for picture-based questions */
  imageUrl?: string;
  /** Optional fallback image URL when primary fails to load */
  fallbackImageUrl?: string;
  /** Whether this is a picture perfect question */
  isPicturePerfect?: boolean;
  /** Game type (e.g. 'are-you-drunk') for drunk-vision animations */
  gameType?: string;
  /** Additional content to display (e.g., for speed challenge) */
  displayContent?: string;
  /** Challenge type for speed challenge */
  challengeType?: string;
  /** Custom render function for question content */
  children?: React.ReactNode;
  /** Custom className */
  className?: string;
}

/**
 * Conditionally displays question content based on whether host is playing.
 * - If host is NOT playing: Only shows answer options (no question text)
 * - If host IS playing: Shows full question content
 */
export const ConditionalQuestionDisplay: React.FC<ConditionalQuestionDisplayProps> = ({
  hostIsPlaying,
  question,
  imageUrl,
  fallbackImageUrl,
  isPicturePerfect,
  gameType,
  displayContent,
  challengeType,
  children,
  className = 'question-container',
}) => {
  const { t } = useTranslation();

  // If host is not playing, don't show question - only show answer options
  if (!hostIsPlaying) {
    return null;
  }

  const imageAlt = isPicturePerfect
    ? (gameType === 'animal-quiz'
        ? t('gameplay.imageAlt.animalQuiz')
        : gameType === 'are-you-drunk'
          ? t('gameplay.imageAlt.guessPicture')
          : t('gameplay.imageAlt.guessWho'))
    : t('gameplay.imageAlt.guessWho');

  const isAreYouDrunk = gameType === 'are-you-drunk';
  const drunkImageClass = isAreYouDrunk ? pickImageAnimationForSeed(question) : '';

  return (
    <div className={className}>
      {/* Image for Picture Perfect game (Play It Loud, Picture Perfect, Are You Drunk, etc.) */}
      {isPicturePerfect && (imageUrl || fallbackImageUrl) && (
        <div style={{ 
          position: 'relative', 
          marginBottom: '1rem', 
          marginTop: '0.5rem',
          width: '100%', 
          textAlign: 'center' 
        }}>
          <div
            className={isAreYouDrunk ? `drunk-img-wrap ${drunkImageClass}` : ''}
            style={{
              display: isAreYouDrunk ? undefined : 'inline-block',
              borderRadius: '12px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
              overflow: 'hidden',
            }}
          >
            <ImageWithFallback
              src={imageUrl || fallbackImageUrl || ''}
              fallbackSrc={fallbackImageUrl}
              alt={imageAlt}
              style={{
                maxWidth: '100%',
                maxHeight: '200px',
                display: 'block',
              }}
            />
          </div>
        </div>
      )}

      {/* Display content for speed challenge */}
      {displayContent && challengeType && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            fontSize: challengeType === 'emoji-identify' || challengeType === 'shape' 
              ? 'clamp(3rem, 7vw, 5rem)' 
              : challengeType === 'type-word' || challengeType === 'reverse'
              ? 'clamp(2rem, 5vw, 3rem)'
              : challengeType === 'math' || challengeType === 'pattern'
              ? 'clamp(1.5rem, 4vw, 2.5rem)'
              : challengeType === 'color-sequence'
              ? 'clamp(2rem, 6vw, 4rem)'
              : 'clamp(1.5rem, 4vw, 2.5rem)',
            lineHeight: '1.2',
            textAlign: 'center',
            padding: '1rem 1.5rem',
            borderRadius: '15px',
            width: '100%',
            minHeight: '80px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: (challengeType === 'type-word' || challengeType === 'reverse' || challengeType === 'math' || challengeType === 'pattern') ? 'monospace' : 'inherit',
            letterSpacing: challengeType === 'type-word' || challengeType === 'reverse' ? '0.15em' : challengeType === 'color-sequence' ? '0.3em' : 'normal',
          }}
          className="question-display__speed-content"
          >
            {displayContent}
          </div>
        </div>
      )}

      {/* Question text (with drunk-vision letter animations for Are You Drunk) */}
      {question && !displayContent && (
        isAreYouDrunk ? (
          <DrunkQuestionText text={question} className="question-text" />
        ) : (
          <h1 className="question-text">{question}</h1>
        )
      )}

      {/* Custom children content */}
      {children}
    </div>
  );
};
